import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad, Route, Router, RouterStateSnapshot
} from '@angular/router';
import { environment } from '@env/environment';
import { UiService } from 'app/dp-world/services/ui.service';
import { UtilsService } from 'app/dp-world/services/utils.service';
import { LogService } from '../../../dp-world/services/log.service';
import { AuthService } from './auth.service';
import { OrganizationType, User } from './user.model';

/**
 * Controls whether users can access routes of not.
 */
@Injectable()
export class AuthGuard implements CanActivate, CanLoad {
  constructor(
    private authService: AuthService,
    private router: Router,
    private logger: LogService,
    private ui: UiService,
    private utils: UtilsService,
  ) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const user: User = this.authService.currentUserValue;
    if (state.url.includes('/create-account')) {
      if (window.location.host === 'tradefinance.dpworld.com' || window.location.host === 'qa-tradefinance.dpworld.com') {
        this.router.navigate(['']);
        return false;
      }
      if (user) {
        localStorage.setItem(environment.storage.returnToHomeDueToFailure, 'true');
        this.router.navigate(['/home/def']);
      } else {
        return true;
      }
    } else if (this.authService.isAuth()) {
      if (this.authService.isLoggedInToAccountingModule()) {
        if (!window.location.href.includes('/acc-rec')) {
          window.location.href = '/acc-rec';
          return;
        } else {
          if (window.location.href.includes('/acc-rec')) {
            window.location.href = '/dashboard';
            return;
          }
        }
      } else {
        const isSCF = localStorage.getItem(environment.storage.scfPlatform) === 'true' ? true : false;
        const isBHS = localStorage.getItem(environment.storage.bhsModule) === 'true' ? true : false;
        if (isSCF) {
          if (!window.location.href.includes('/scf')) {
            window.location.href = '/scf/program';
            return;
          }
        } else if (isBHS) {
          if (!window.location.href.includes('/bhs')) {
            window.location.href = '/bhs';
            return;
          }
        } else {
          if (window.location.href.includes('/scf')) {
            window.location.href = '/dashboard';
            return;
          }
        }
      }


      const tenantCode = this.authService.getTenantCode();
      if (!user || !this.utils.isAccountFullySetup(user, tenantCode)) {
        localStorage.setItem(environment.storage.returnToHomeDueToFailure, 'true');
        this.router.navigate(['/home/def']);
      }


      if (state.url.includes('credit-policy') && !user.credit_policy_allowed) {
        this.logout();
      }

      if (state.url.includes('cargoes-bl')) {
        if (user.organisation_type === 'DTF') {
          this.logout();
        }
      }

      if (state.url.includes('enable-email-access')) {
        if (user.organisation_type !== 'DTF' || environment.environmentName === 'Prod' || environment.environmentName === 'Demo' || environment.environmentName === 'Uat') {
          this.logout();
        }
      }

      // retrict user if all required terms and conditions are not accepted
      if (user.organisation_type === 'BANK' || user.organisation_type === 'ORG') {
        const isClientOrFiAgreementsAccepted = localStorage.getItem(environment.storage.clientOrFiAgreementsAccepted);
        if (!isClientOrFiAgreementsAccepted
          && !this.authService.isTermsAccepted()
          && !this.authService.validateUserAgreements(tenantCode)) {
          localStorage.setItem(environment.storage.returnToHomeDueToFailure, 'true');
          this.router.navigate(['/home/def']);
        }
      }
      // restrict admin only pages.
      if (route.data && route.data.adminOnly) {
        // console.log("canActivate; user: " + JSON.stringify(user));
        // if the user object is missing of misformed
        if (!user || user.userCode === null) {
          this.logger.logError('AuthGuard.canActivate; !user');
          this.ui.snackbar('You are not authorized to access that page.');
          return false;
        } else if (!user.isAdmin) {
          // console.log("canActivate; user is not authorized to access an admin page.");
          this.ui.snackbar('You are not authorized to access that page.');
          return false;
        }
      }

      // restrict views that only DTF admin should access
      if (route.data && route.data.onlyDTF) {
        if (user.organisation_type !== 'DTF') {
          this.logout();
        }
      }

      if (route.data && route.data.onlyDTFOm) {
        if (user.organisation_type == 'ORG' || (user.organisation_type === 'BANK' && !state.url.includes('onboarding'))) {
          this.logout();
        } else if (user.organisation_type === 'DTF') {
          if (state.url.includes('onboarding')) {
            if (user.user_department === environment.FINANCE_DEPARTMENT_TYPES.COMPLIANCE) {
              this.logout();
            }
          } else {
            if (!(user.user_department === environment.FINANCE_DEPARTMENT_TYPES.OPERATIONS)) {
              this.logout();
            }
          }
        }
      }

      if (route.data && route.data.onlyDTF_OM_AND_CO) {
        if (user.user_department === null) {
          this.logout();
        } else {
          const isNavRestricted = user.user_department !== environment.FINANCE_DEPARTMENT_TYPES.OPERATIONS
            && ((user.user_department !== environment.FINANCE_DEPARTMENT_TYPES.COMPLIANCE && (route.routeConfig.path === 'calc-risk-rating' || route.routeConfig.path === 'kyc-expiry-renewals'))
              || (route.routeConfig.path === 'update-rating-params') || (route.routeConfig.path === 'reports'));
          if (isNavRestricted) {
            this.logout();
          }
        }
      }

      if (state.url.includes('companyReference')) {
        let url = decodeURIComponent(state.url);
        const index = url.indexOf('companyReference');
        const compRefStrLength = 17; // length of companyReference=
        const companyReferenceId = url.substring((index + compRefStrLength), url.length)
        if (companyReferenceId) {
          if (url.startsWith('/')) {
            url = url.substring(1);
          }
          const requestUrlWithoutCompReference = url.substring(0, (index - 2))
          this.authService.triggerOrgSwitch(atob(companyReferenceId), 'encodedMawaniId', requestUrlWithoutCompReference);
        }
        this.router.navigate(['/dashboard']);
        return false;
      } else if (state.url.includes('download-cf-review-report') && user.is_lending_organisation) {
        return true;
      }

      //check right access while routing
      if (!state.url.includes('sessions') && !state.url.includes('dashboard') && !this.authService.checkAndValidateRoutePrivilege(state.url, user.organisation_type)) {
        this.router.navigate(['/dashboard']);
        setTimeout(() => {
          this.ui.closeCurrentSnackbar();
          this.ui.snackbar(`User doesn't have permissions for this service, please request admin for the rights.`);
        }, 0);
        return false;
      }
      if (user?.user_department === OrganizationType.ORG && state.url.includes('scf') && !user?.allowed_services?.supply_chain_financing) {
        this.logout();
      }
      return true;
    } else if (state.url.includes('sessions') && this.authService.isOnlyVerified()) {
      return true;
    } else if ((state.url.includes('faq/def') || state.url.includes('user-manual/def') || state.url.includes('release-notes/def')) && this.authService.isOnlyVerified()) {
      return true;
    } else if ((state.url === '/register-company/def' || state.url === '/register-company/update') && this.authService.isOnlyVerified() && user?.companyStatus?.name && this.authService.pendingStatuses.includes(user.companyStatus.name)) {
      return true;
    } else {
      localStorage.setItem(environment.storage.returnToHomeDueToFailure, 'true');
      this.router.navigate(['/home/def'], {
        queryParams: { returnUrl: state.url },
      });
    }
  }

  private logout(): void {
    this.authService.logout();
    setTimeout(() => {
      this.ui.closeCurrentSnackbar();
      this.ui.snackbar('You are not authorized to access that page.');
    }, 0);
  }
  /**
   * called when routing is trying to determine if a routes assets should be
   * lazy loaded
   * @param route - route
   */
  canLoad(route: Route) {
    return true;
    /*
    if (this.authService.isAuth()) {
      return true;
    } else {
      this.router.navigate(['/login']);
    }
    */
  }
}
